import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as hotelsApi from './hotels.api';

import type {
  CreateHotelInventoryRequest,
  CreateHotelRequest,
  CreateHotelRoomRequest,
  HotelApprovalRequest,
  HotelInventoryListQuery,
  HotelListQuery,
  UpdateHotelInventoryRequest,
  UpdateHotelRequest,
  UpdateHotelRoomRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: HotelListQuery) => ['hotels', 'list', query] as const,
  facets: ['hotels', 'facets'] as const,
  detail: (id: string) => ['hotels', 'detail', id] as const,
  rooms: (id: string) => ['hotels', 'rooms', id] as const,
  inventory: (id: string, q: HotelInventoryListQuery) => ['hotels', 'inventory', id, q] as const,
};

export const useHotelsList = (query: HotelListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => hotelsApi.listHotels(query),
    staleTime: 30_000,
  });

/**
 * Facets (in-use countries + cities) for the Country / City list filters.
 * Keyed under ['hotels'] so create/update/delete invalidations refresh it - a
 * hotel added in a new city shows up in the filter automatically.
 */
export const useHotelFacets = () =>
  useQuery({
    queryKey: QK.facets,
    queryFn: hotelsApi.getHotelFacets,
    staleTime: 60_000,
  });

export const useHotelDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => hotelsApi.getHotel(id as string),
    staleTime: 30_000,
    enabled: Boolean(id),
  });

export const useCreateHotel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateHotelRequest) => hotelsApi.createHotel(payload),
    onSuccess: (hotel) => {
      qc.invalidateQueries({ queryKey: ['hotels', 'list'] });
      toast.success(`Added ${hotel.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add hotel'),
  });
};

export const useUpdateHotel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateHotelRequest }) =>
      hotelsApi.updateHotel(id, payload),
    onSuccess: (hotel) => {
      qc.invalidateQueries({ queryKey: ['hotels', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(hotel.id) });
      toast.success('Hotel updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update hotel'),
  });
};

export const useApproveHotel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: HotelApprovalRequest }) =>
      hotelsApi.approveHotel(id, payload),
    onSuccess: (hotel, vars) => {
      qc.invalidateQueries({ queryKey: ['hotels', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(hotel.id) });
      toast.success(vars.payload.approve ? 'Hotel approved' : 'Approval revoked');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to change approval'),
  });
};

export const useDeleteHotel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hotelsApi.deleteHotel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hotels', 'list'] });
      toast.success('Hotel deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete hotel'),
  });
};

export const useDuplicateHotel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => hotelsApi.duplicateHotel(id),
    onSuccess: (hotel) => {
      qc.invalidateQueries({ queryKey: ['hotels', 'list'] });
      toast.success(`Duplicated as "${hotel.name}" (draft)`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to duplicate hotel'),
  });
};

export const useBulkDeleteHotels = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => hotelsApi.bulkDeleteHotels(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hotels'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete hotels'),
  });
};

// ----- Rooms -----

export const useHotelRooms = (hotelId: string | null | undefined) =>
  useQuery({
    queryKey: QK.rooms(hotelId ?? ''),
    queryFn: () => hotelsApi.listHotelRooms(hotelId as string),
    staleTime: 30_000,
    enabled: Boolean(hotelId),
  });

export const useCreateHotelRoom = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateHotelRoomRequest) => hotelsApi.createHotelRoom(hotelId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.rooms(hotelId) });
      qc.invalidateQueries({ queryKey: QK.detail(hotelId) });
      qc.invalidateQueries({ queryKey: ['hotels', 'list'] });
      toast.success('Room added');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add room'),
  });
};

export const useUpdateHotelRoom = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, payload }: { roomId: string; payload: UpdateHotelRoomRequest }) =>
      hotelsApi.updateHotelRoom(hotelId, roomId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.rooms(hotelId) });
      qc.invalidateQueries({ queryKey: QK.detail(hotelId) });
      toast.success('Room updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update room'),
  });
};

export const useDeleteHotelRoom = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => hotelsApi.deleteHotelRoom(hotelId, roomId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.rooms(hotelId) });
      qc.invalidateQueries({ queryKey: QK.detail(hotelId) });
      qc.invalidateQueries({ queryKey: ['hotels', 'list'] });
      toast.success('Room removed');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to remove room'),
  });
};

// ----- HotelInventory hooks (B4.12) -----

export const useHotelInventory = (
  hotelId: string | null | undefined,
  query: HotelInventoryListQuery,
) =>
  useQuery({
    queryKey: QK.inventory(hotelId ?? '', query),
    queryFn: () => hotelsApi.listHotelInventory(hotelId as string, query),
    staleTime: 30_000,
    enabled: Boolean(hotelId),
  });

const invalidateInventory = (qc: ReturnType<typeof useQueryClient>, hotelId: string) => {
  qc.invalidateQueries({ queryKey: ['hotels', 'inventory', hotelId] });
  qc.invalidateQueries({ queryKey: QK.detail(hotelId) });
};

export const useCreateHotelInventory = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateHotelInventoryRequest) =>
      hotelsApi.createHotelInventory(hotelId, payload),
    onSuccess: () => {
      invalidateInventory(qc, hotelId);
      toast.success('Inventory rate card added');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to add inventory rate card'),
  });
};

export const useUpdateHotelInventory = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inventoryId,
      payload,
    }: {
      inventoryId: string;
      payload: UpdateHotelInventoryRequest;
    }) => hotelsApi.updateHotelInventory(hotelId, inventoryId, payload),
    onSuccess: () => {
      invalidateInventory(qc, hotelId);
      toast.success('Inventory rate card updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update inventory rate card'),
  });
};

export const useDeleteHotelInventory = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inventoryId: string) => hotelsApi.deleteHotelInventory(hotelId, inventoryId),
    onSuccess: () => {
      invalidateInventory(qc, hotelId);
      toast.success('Inventory rate card removed');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to remove inventory rate card'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
